// Turning a failed request into something worth reading.
//
// Every service used to collapse each failure into its own fallback sentence —
// "Failed to load integrations", "Failed to load services". That sentence is
// right for a server error and WRONG for a refusal: a business admin looking at
// "Failed to load services" reasonably concludes the product is broken and
// reports an outage, when the truth is that their role is not permitted and no
// amount of retrying will change it.
//
// That is not hypothetical. /v1/crm/builtin and /v1/crm/providers listed
// BUSINESS_ADMIN but not OPERATOR_ADMIN — the same role under its other name —
// so a tenant's own admin got 403 on their own bookings, and the screen said
// "Failed to load".
//
// So the status decides the words. The server's own message still wins when it
// sends one, because it knows more than we do.

export interface ApiFailure {
  success: false;
  error: string;
  /** The HTTP status, so a caller can style a refusal differently from an outage. */
  status?: number;
  /** True for 401/403 — the request was understood and declined. */
  denied?: boolean;
}

/**
 * @param e   the axios error
 * @param msg what to say when nothing better is known (the old behaviour)
 */
export const apiError = (e: any, msg: string): ApiFailure => {
  const status: number | undefined = e?.response?.status;
  const serverMessage: string | undefined =
    e?.response?.data?.message || e?.response?.data?.error;

  // A refusal is never "could not load".
  if (status === 403) {
    return {
      success: false,
      status,
      denied: true,
      error: serverMessage || 'You do not have access to this. Ask an administrator to grant your role access.',
    };
  }
  if (status === 401) {
    // The axios interceptor normally redirects to /login before this is read;
    // worth wording correctly for the cases where it does not.
    return {
      success: false,
      status,
      denied: true,
      error: serverMessage || 'Your session has expired. Sign in again.',
    };
  }
  if (status === 404) {
    return {
      success: false,
      status,
      error: serverMessage || 'Not available on this server.',
    };
  }
  // No response at all: the request never got an answer. Distinct from a
  // server error, and the distinction is what tells someone whether to retry.
  if (!status) {
    return {
      success: false,
      error: 'Could not reach the server. Check your connection and try again.',
    };
  }
  return { success: false, status, error: serverMessage || msg };
};
